import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { BookOpen, RotateCcw, Clock, MessageCircle, CheckCircle2, XCircle } from "lucide-react";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <p className="text-xl font-bold font-heading">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

export default function Statistics() {
  const { data: today } = useQuery({ queryKey: ["stats-today"], queryFn: async () => (await api.get("/stats/today")).data });
  const { data: overall } = useQuery({ queryKey: ["stats-overall"], queryFn: async () => (await api.get("/stats/overall")).data });
  const { data: charts = [] } = useQuery({ queryKey: ["stats-charts"], queryFn: async () => (await api.get("/stats/charts")).data });
  const { data: hardest } = useQuery({ queryKey: ["stats-hardest"], queryFn: async () => (await api.get("/stats/hardest-words")).data });

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-8" data-testid="statistics-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Статистика</h1>
        <p className="text-sm text-muted-foreground mt-1">Ваш прогресс в изучении языка</p>
      </div>

      <section>
        <h2 className="font-heading text-lg font-semibold mb-3">Сегодня</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={BookOpen} label="Изучено слов" value={today?.words_added || 0} testid="stat-today-words" />
          <StatCard icon={RotateCcw} label="Повторено карточек" value={today?.cards_reviewed || 0} testid="stat-today-reviewed" />
          <StatCard icon={Clock} label="Минут просмотра" value={Math.round(today?.minutes_watched || 0)} testid="stat-today-watched" />
          <StatCard icon={MessageCircle} label="Минут с AI" value={Math.round(today?.minutes_ai || 0)} testid="stat-today-ai" />
          <StatCard icon={CheckCircle2} label="Правильных" value={today?.correct_answers || 0} testid="stat-today-correct" />
          <StatCard icon={XCircle} label="Ошибок" value={today?.wrong_answers || 0} testid="stat-today-wrong" />
        </div>
      </section>

      <section>
        <h2 className="font-heading text-lg font-semibold mb-3">Общая статистика</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={BookOpen} label="Всего слов" value={overall?.total_words || 0} testid="stat-overall-total" />
          <StatCard icon={RotateCcw} label="Выучено" value={overall?.mastered || 0} testid="stat-overall-mastered" />
          <StatCard icon={Clock} label="Часов просмотра" value={overall?.hours_watched || 0} testid="stat-overall-hours-watched" />
          <StatCard icon={MessageCircle} label="Часов практики" value={overall?.hours_practice || 0} testid="stat-overall-hours-practice" />
        </div>
      </section>

      <section>
        <h2 className="font-heading text-lg font-semibold mb-3">Активность по дням</h2>
        <div className="bg-card border border-border rounded-xl p-4 h-72" data-testid="activity-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={charts}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="words_added" stroke="hsl(var(--chart-1))" name="Слова" strokeWidth={2} />
              <Line type="monotone" dataKey="cards_reviewed" stroke="hsl(var(--chart-2))" name="Повторения" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="grid sm:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-4" data-testid="hardest-words-list">
          <h3 className="font-heading font-semibold mb-2">Самые сложные слова</h3>
          {hardest?.hardest?.map((w) => (
            <div key={w.id} className="flex justify-between text-sm py-1">
              <span>{w.word}</span><span className="text-muted-foreground">{w.lapses} ошибок</span>
            </div>
          ))}
        </div>
        <div className="bg-card border border-border rounded-xl p-4" data-testid="easiest-words-list">
          <h3 className="font-heading font-semibold mb-2">Самые лёгкие слова</h3>
          {hardest?.easiest?.map((w) => (
            <div key={w.id} className="flex justify-between text-sm py-1">
              <span>{w.word}</span><span className="text-muted-foreground">{w.repetitions} повторений</span>
            </div>
          ))}
        </div>
        <div className="bg-card border border-border rounded-xl p-4" data-testid="stale-words-list">
          <h3 className="font-heading font-semibold mb-2">Давно не повторялись</h3>
          {hardest?.stale?.map((w) => (
            <div key={w.id} className="flex justify-between text-sm py-1">
              <span>{w.word}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
